import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Menu from './components/Menu';
import Sidebar from './components/Sidebar';
import AppRoutes from './router/AppRoutes';
import Login from './pages/Login';
import LandingPage from './pages/LandingPage';
import RecuperarSenha from './pages/RecuperarSenha';
import ModalEmailPendente from './components/ModalEmailPendente';
import ModalAssinatura from './components/ModalAssinatura';
import ModalApp from './components/ModalApp';
import { useAuth } from './hooks/useAuth';
import { plataformaSmartphone } from './utils/dispositivo';
import useNotificacoesNaoLidas from './hooks/useNotificacoesNaoLidas';
import { api } from './api';
import ToastContainer from './components/Toast';
import './App.css';
import './components/Sidebar.css';

// Detecta se está rodando como app instalado (PWA standalone, TWA, ou aberto
// pelo manifest com ?source=pwa). Funciona em qualquer navegador.
const isPWA = () =>
  window.matchMedia('(display-mode: standalone)').matches
  || window.navigator.standalone
  || document.referrer.includes('android-app://')
  || window.matchMedia('(display-mode: minimal-ui)').matches
  || window.location.search.includes('source=pwa');

// Rota "/" — navegador mostra a landing; PWA abre direto no login
function LandingRoute({ onIrLogin }) {
  if (isPWA()) return <Navigate to="/login" replace />;
  return <LandingPage onIrLogin={onIrLogin} />;
}

/** Barra de navegação fixa no rodapé — atalhos rápidos. */
function BottomNav({ naoLidas }) {
  const location = useLocation();
  const navigate = useNavigate();
  const current = location.pathname;

  const items = [
    { path: '/minha-assinatura', label: 'Assinatura', icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
        <line x1="1" y1="10" x2="23" y2="10"/>
      </svg>
    )},
    { path: '/', label: 'Início', icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
        <polyline points="9 22 9 12 15 12 15 22"/>
      </svg>
    )},
    { path: '/notificacoes', label: 'Avisos', badge: naoLidas, icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
        <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
      </svg>
    )},
  ];

  return (
    <nav className="bottom-nav" aria-label="Navegação rápida">
      {items.map(item => (
        <button
          key={item.path}
          className={`bottom-nav__item ${current === item.path ? 'bottom-nav__item--active' : ''}`}
          onClick={() => navigate(item.path)}
          aria-label={item.badge ? `${item.label} (${item.badge} nova${item.badge > 1 ? 's' : ''})` : item.label}
          aria-current={current === item.path ? 'page' : undefined}
        >
          <span className="bottom-nav__icone">
            {item.icon}
            {item.badge > 0 && (
              <span className="bottom-nav__badge">{item.badge > 9 ? '9+' : item.badge}</span>
            )}
          </span>
          <span className="bottom-nav__label">{item.label}</span>
        </button>
      ))}
    </nav>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppInner />
    </BrowserRouter>
  );
}

function AppInner() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logado, sessaoMsg, setSessaoMsg, login, logout, atualizarPendentes } = useAuth();
  const naoLidas = useNotificacoesNaoLidas(logado);
  const [menuOpen, setMenuOpen] = useState(false);
  const [emailModal, setEmailModal] = useState(() => localStorage.getItem('emailPendente') === 'true');
  const [assinaturaBloqueada, setAssinaturaBloqueada] = useState(false);
  const [assinaturaData, setAssinaturaData] = useState(null);
  // Convite para usar o app no celular. Guarda a plataforma ('android'|'ios')
  // quando deve aparecer; null quando não.
  const [plataformaApp, setPlataformaApp] = useState(null);

  // O painel /admin é o caminho para liberar contas vencidas: um 402 vindo de
  // qualquer chamada enquanto o master está nele não pode disparar o bloqueio,
  // senão o próprio dono fica trancado para fora da ferramenta de desbloqueio.
  const estaNoAdmin = location.pathname === '/admin';

  // Ref para o handler enxergar a rota atual sem precisar se reinscrever a cada
  // navegação (o listener é registrado uma única vez).
  const estaNoAdminRef = useRef(estaNoAdmin);
  useEffect(() => { estaNoAdminRef.current = estaNoAdmin; }, [estaNoAdmin]);

  useEffect(() => {
    const handler = () => {
      if (estaNoAdminRef.current) return; // no /admin, ignora o 402
      api.getAssinatura().then(data => {
        setAssinaturaData(data);
        setAssinaturaBloqueada(true);
      }).catch(() => setAssinaturaBloqueada(true));
    };
    window.addEventListener('assinatura-vencida', handler);
    return () => window.removeEventListener('assinatura-vencida', handler);
  }, []);

  // Convite do app: só em smartphone (notebook/tablet ficam de fora), no máximo
  // 3 vezes por usuário, e nunca quando o AgriGest já roda como PWA/app — nesse
  // caso o convite seria redundante. A contagem é por usuário para que trocar de
  // conta no mesmo aparelho não herde o "já viu 3 vezes" de outra pessoa.
  useEffect(() => {
    if (!logado || isPWA()) return;

    const plataforma = plataformaSmartphone();
    if (!plataforma) return;

    const usuario = localStorage.getItem('usuario') || 'anon';
    const chave = `app.modalAppVistas.${usuario}`;
    const vistas = parseInt(localStorage.getItem(chave) || '0', 10) || 0;
    if (vistas >= 3) return;

    // Pequeno atraso para o modal não competir com a transição de entrada da
    // tela inicial logo após o login.
    const t = setTimeout(() => {
      localStorage.setItem(chave, String(vistas + 1));
      setPlataformaApp(plataforma);
    }, 1200);
    return () => clearTimeout(t);
  }, [logado]);

  const handleLoginSuccess = () => {
    login();
    setEmailModal(localStorage.getItem('emailPendente') === 'true');
    navigate('/');
  };

  // Link do email de recuperação: sempre fora do layout autenticado,
  // mesmo se houver token antigo no localStorage.
  if (location.pathname === '/recuperar-senha') {
    return <RecuperarSenha />;
  }

  if (!logado) {
    return (
      <>
        {sessaoMsg && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
            background: '#c62828', color: 'white', padding: '1rem',
            textAlign: 'center', fontSize: '1rem', fontWeight: 600
          }}>
            Sessão expirada. Faça login novamente.
            <button onClick={() => setSessaoMsg(false)} style={{
              marginLeft: '1rem', background: 'rgba(255,255,255,0.3)',
              border: 'none', color: 'white', padding: '0.2rem 0.8rem',
              borderRadius: 4, cursor: 'pointer'
            }}>✕</button>
          </div>
        )}
        <Routes>
          <Route path="/login" element={<Login telaInicial="login" onLogin={handleLoginSuccess} />} />
          <Route path="/cadastro" element={<Login telaInicial="criar" onLogin={handleLoginSuccess} />} />
          <Route path="/recuperar-senha" element={<RecuperarSenha />} />
          <Route path="/" element={
            <LandingRoute onIrLogin={(modo) => navigate(modo === 'criar' ? '/cadastro' : '/login')} />
          } />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </>
    );
  }

  return (
    <div className="app-layout">
      {emailModal && <ModalEmailPendente onClose={() => setEmailModal(false)} />}
      {assinaturaBloqueada && assinaturaData && !estaNoAdmin && (
        <ModalAssinatura assinatura={assinaturaData} onClose={() => {}} />
      )}
      {/* Convite do app no celular — não concorre com os modais acima. */}
      {plataformaApp && !emailModal && !assinaturaBloqueada && (
        <ModalApp plataforma={plataformaApp} onClose={() => setPlataformaApp(null)} />
      )}
      <Sidebar onLogout={logout} pendentes={naoLidas} />
      <Header onMenuOpen={() => setMenuOpen(true)} pendentes={naoLidas} />
      <Menu
        open={menuOpen}
        onClose={() => { setMenuOpen(false); atualizarPendentes(); }}
        onLogout={logout}
        pendentes={naoLidas}
      />
      <main className="main-content">
        <AppRoutes />
      </main>
      <BottomNav naoLidas={naoLidas} />
      <ToastContainer />
    </div>
  );
}
